import { useBunja } from 'bunja/react';
import { useAtom, useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import {
  ClearableNumberInputWithFrame,
  showClearButtonWhenValueIsNotZero,
} from '@flipster/features/src/input/clearable-number-input';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { createDropdownSelect } from '@flipster/react/design-system/dropdown/dropdown-select';
import { snackbar } from '@flipster/react/design-system/snackbar';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { css } from '@flipster/styled-system/css';
import { flex, hstack, vstack } from '@flipster/styled-system/patterns';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

import { formBunja, type TriggerExecutionType } from './bunja/form';
import { priceFormSegmentBunja } from './bunja/price-form';

const ExecutionTypeDropdown = createDropdownSelect<TriggerExecutionType>();

const OrderPriceForm = () => {
  const t = useTranslations();
  const transTrade = useTranslations('Trade.r250401');
  const transTradeLimit = useTranslations('Trade.r251104');
  const transTpsl = useTranslations('Trade.r260318');
  const { symbolInfoWithDefaultAtom } = useBunja(tradeSymbolBunja);
  const symbolInfo = useAtomValue(symbolInfoWithDefaultAtom);
  const { triggerExecutionTypeAtom, limitPriceAtom, isBboAtom, resetLimitPrice, tpSlAtom } = useBunja(formBunja);
  const [executionType, setExecutionType] = useAtom(triggerExecutionTypeAtom);
  const [limitPrice, setLimitPrice] = useAtom(limitPriceAtom);
  const [isBbo, setIsBbo] = useAtom(isBboAtom);
  const { enabled: isTpslEnabled } = useAtomValue(tpSlAtom);
  const { trackTabPriceClearClick, trackTabPriceEditClick } = useBunja(priceFormSegmentBunja);

  const { pricePrecision } = symbolInfo;
  const isLimit = executionType === 'limit';
  const isBboDisabled = isTpslEnabled;
  const isInputDisabled = !isLimit || isBbo;
  const disabledInputPlaceholder = isLimit ? transTradeLimit('Best_counterparty') : transTrade('Market_price');

  const executionTypeLabel: Record<TriggerExecutionType, string> = {
    market: transTrade('Tab_market'),
    limit: transTradeLimit('Limit'),
  };

  return (
    <div className={vstack({ w: '100%', gap: 8, alignItems: 'stretch' })}>
      <div className={flex({ justify: 'space-between', align: 'center', h: 24 })}>
        <div className={css({ textStyle: 'Text/L/Strong' })}>{t('Autogen_common.r251126.s_Order_price')}</div>
        {isLimit && (
          <div
            onClick={() => {
              if (isBboDisabled) snackbar.error({ message: transTpsl('Bbo_disabled_description') });
            }}
          >
            <Checkbox
              checked={isBbo}
              disabled={isBboDisabled}
              onChange={(e) => {
                setIsBbo(e.target.checked);
                if (!e.target.checked) resetLimitPrice();
              }}
            >
              <div className={hstack({ gap: 4, alignItems: 'center', textStyle: 'Text/L/Strong' })}>BBO</div>
            </Checkbox>
          </div>
        )}
      </div>
      <div className={hstack({ gap: 8, w: '100%', alignItems: 'center' })}>
        <div className={css({ flex: 1, minW: 0 })}>
          {isInputDisabled ? (
            <div
              className={flex({
                align: 'center',
                height: 48,
                px: 12,
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'border.default.level-2',
                borderRadius: 4,
                bg: 'background.default.level-1',
                color: 'content.default.level-1',
                textStyle: 'Text/L/Regular',
              })}
              data-testid='order-price-disabled-placeholder'
            >
              {disabledInputPlaceholder}
            </div>
          ) : (
            <ClearableNumberInputWithFrame
              determineShowClearButton={showClearButtonWhenValueIsNotZero}
              value={limitPrice}
              precision={pricePrecision}
              onChange={(p) => setLimitPrice(p || '0')}
              onFocus={trackTabPriceEditClick}
              onClearClick={trackTabPriceClearClick}
            />
          )}
        </div>
        <ExecutionTypeDropdown.Root
          selected={executionType}
          onChange={(value) => {
            setExecutionType(value);
            if (value === 'market' && isBbo) {
              setIsBbo(false);
            }
          }}
        >
          <ExecutionTypeDropdown.Trigger
            css={hstack.raw({
              h: 48,
              flexShrink: 0,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'border.default.level-2',
              borderRadius: 4,
              px: 12,
              alignItems: 'center',
              justify: 'space-between',
              gap: 10,
              textStyle: 'Text/L/Strong',
              color: 'content.default.level-4',
              whiteSpace: 'nowrap',
            })}
          >
            {executionTypeLabel[executionType]}
            <ChevronDownIcon
              size={16}
              className={css({
                flexShrink: 0,
                transition: 'transform 0.2s',
                '[aria-expanded=true] &': { transform: 'rotate(-180deg)' },
              })}
            />
          </ExecutionTypeDropdown.Trigger>
          <ExecutionTypeDropdown.Select portal>
            <ExecutionTypeDropdown.Item value='market' label={executionTypeLabel.market} />
            <ExecutionTypeDropdown.Item value='limit' label={executionTypeLabel.limit} />
          </ExecutionTypeDropdown.Select>
        </ExecutionTypeDropdown.Root>
      </div>
    </div>
  );
};

export default OrderPriceForm;
